import { DEFAULT_OG_IMAGE, SITE_URL } from "../../components/Seo/Seo";
import { blocksToPlainText } from "../richText";

/** @typedef {import("./index").ResourceArticle} ResourceArticle */

const ORG = {
  "@type": "Organization",
  name: "Listino",
  url: `${SITE_URL}/`,
  logo: { "@type": "ImageObject", url: `${SITE_URL}/listino-logo.png` },
};

/**
 * Build the JSON-LD array for a resource article.
 * @param {ResourceArticle} article
 * @returns {Record<string, unknown>[]}
 */
export const articleJsonLd = (article) => {
  const url = `${SITE_URL}/resources/${article.slug}`;
  const blocks = [];
  /** @type {Record<string, unknown>[]} */

  blocks.push({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: ORG,
    publisher: ORG,
    image: article.image || DEFAULT_OG_IMAGE,
    mainEntityOfPage: url,
  });

  blocks.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/resources` },
      { "@type": "ListItem", position: 3, name: article.title, item: url },
    ],
  });

  if (article.faqs?.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: blocksToPlainText(f.answer) },
      })),
    });
  }

  // Optional page-specific schema (e.g. DefinedTermSet for the glossary,
  // SoftwareApplication for a tool explainer).
  if (article.extraJsonLd?.length) {
    blocks.push(...article.extraJsonLd);
  }

  return blocks;
};

/**
 * JSON-LD for the /resources hub.
 * @param {ResourceArticle[]} resources
 * @returns {Record<string, unknown>[]}
 */
export const resourcesIndexJsonLd = (resources) => [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Listino Resources",
    url: `${SITE_URL}/resources`,
    description:
      "Guides and data on listing optimization, pricing, photos, and winning listings — for real estate agents and home sellers.",
    hasPart: resources.map((a) => ({
      "@type": "Article",
      headline: a.title,
      url: `${SITE_URL}/resources/${a.slug}`,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/resources` },
    ],
  },
];
